import { format, isValid } from 'date-fns/fp';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DateRangePicker } from '../date/DateRangePicker';
import { Icon } from '../icon/Icon';
import { Input } from './Input';
import { themeGet } from '@styled-system/theme-get';

const Wrapper = styled.div`
  position: relative;
  min-width: 480px;
  max-width: 600px;
  width: 100%;
`;

const DateRangeInputWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DateRangePickerWrapper = styled.div`
  position: absolute;
  width: 100%;
  background-color: ${themeGet('dateRangeInput.backgroundColor')};
  border: ${themeGet('dateRangeInput.border')};
  border-radius: ${themeGet('dateInput.borderRadius')};
  box-shadow: ${themeGet('dateRangeInput.boxShadow')};
  z-index: ${themeGet('dateRangeInput.zIndex')};
`;

const Divider = styled.div`
  display: inline-block;
  margin: 0 8px;
`;

const Popover = ({ children, visible, onClose }) => {
  const popoverRef = useRef(null);

  const handleBackDropClick = useCallback(
    event => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose(event);
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleBackDropClick);

    return () => {
      document.removeEventListener('mousedown', handleBackDropClick);
    };
  }, [handleBackDropClick]);

  return <div ref={popoverRef}>{visible && children}</div>;
};

export const DateRangeInput = ({
  value,
  onChange,
  disabled,
  defaultValue,
  dateFormat,
  maxDate,
  minDate,
  ...restProps
}) => {
  const [visible, setVisible] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || defaultValue);
  const [from, to] = currentValue.map(date => (isValid(date) ? format(dateFormat, date) : ''));

  const handleChange = useCallback(
    event => {
      if (onChange) {
        onChange(event);
      }

      if (value === undefined) {
        setCurrentValue(event.target.value);
      }
    },
    [onChange, setCurrentValue, value]
  );

  useEffect(() => {
    if (Array.isArray(value) && value !== currentValue) {
      setCurrentValue(value);
    }
  }, [currentValue, value]);

  return (
    <Wrapper>
      <DateRangeInputWrapper>
        <Input
          {...restProps}
          value={from}
          onFocus={useCallback(() => !disabled && setVisible(true), [disabled])}
          placeholder={dateFormat}
          suffix={<Icon name="CALENDAR" />}
          disabled={disabled}
          readOnly
        />
        <Divider>
          <Icon name="ARROW_RIGHT" />
        </Divider>
        <Input
          {...restProps}
          value={to}
          onFocus={useCallback(() => !disabled && setVisible(true), [disabled])}
          placeholder={dateFormat}
          suffix={<Icon name="CALENDAR" />}
          disabled={disabled}
          readOnly
        />
      </DateRangeInputWrapper>
      <Popover visible={visible} onClose={useCallback(() => setVisible(false), [])}>
        <DateRangePickerWrapper>
          <DateRangePicker onChange={handleChange} maxDate={maxDate} minDate={minDate} />
        </DateRangePickerWrapper>
      </Popover>
    </Wrapper>
  );
};

DateRangeInput.defaultProps = {
  defaultValue: [null, null],
  dateFormat: 'yyyy-MM-dd',
};
